import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { SinisterService } from "../../../../shared/services/api/sinister/SinisterService";
import { trackAsync } from "../../../../shared/utils/telemetry";
import { SinisterSector } from "../../constants/sectorOptions";
import { STAGE_OPTIONS_BY_SECTOR, SinisterStage, getStageLabel } from "../../constants/stageOptions";
import { FollowUpGenerationOptions } from "../FollowUpGenerationOptions";
import { generateFollowUps } from "../../utils/generateFollowUps";

interface Props {
	open: boolean;
	sinisterId: string;
	sector: SinisterSector;
	currentStage?: SinisterStage;
	onClose: () => void;
	onConfirm: (stage: SinisterStage) => void;
}

export const ModalChangeStage = ({ open, sinisterId, sector, currentStage, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const stageOptions = STAGE_OPTIONS_BY_SECTOR[sector];
	const [stage, setStage] = useState<SinisterStage>(currentStage ?? stageOptions[0].value);
	const [generateFollowUp, setGenerateFollowUp] = useState(true);
	// Follow up externo é visível pro sinistro e pra imobiliária (e dispara e-mail aos contatos
	// cadastrados) — por isso começa desmarcado, é uma escolha explícita de cada lançamento.
	const [generateExternalFollowUp, setGenerateExternalFollowUp] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (open) {
			setStage(currentStage && stageOptions.some((option) => option.value === currentStage) ? currentStage : stageOptions[0].value);
			setGenerateFollowUp(true);
			setGenerateExternalFollowUp(false);
		}
	}, [open, sector, currentStage]);

	const handleConfirm = async () => {
		setIsSubmitting(true);
		const result = await trackAsync("sinister.change_stage", () => SinisterService.updateSinisterEtapa(sinisterId, stage), { sinisterId });

		if (result instanceof Error) {
			setIsSubmitting(false);
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		await generateFollowUps({
			sinisterId,
			note: `<p>Etapa atualizada para: ${getStageLabel(stage)}</p>`,
			generateInternal: generateFollowUp,
			generateExternal: generateExternalFollowUp,
			trackLabel: "change_stage",
		});

		setIsSubmitting(false);
		onConfirm(stage);
		toastHandler.present({
			type: "success",
			messageOrContent: `Etapa atualizada para ${getStageLabel(stage)}.`,
			position: "top-right",
		});
		onClose();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Alterar Etapa
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Typography variant="body2" color="text.secondary">
							Etapas disponíveis para o setor atual ({sector === "juridico" ? "Jurídico" : "Cobrança"}).
						</Typography>

						<TextField select label="Etapa" value={stage} onChange={(e) => setStage(e.target.value as SinisterStage)} fullWidth>
							{stageOptions.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>

						<FollowUpGenerationOptions
							generateInternal={generateFollowUp}
							onChangeInternal={setGenerateFollowUp}
							generateExternal={generateExternalFollowUp}
							onChangeExternal={setGenerateExternalFollowUp}
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Cancelar</Button>
					<Button variant="contained" disabled={isSubmitting || stage === currentStage} onClick={handleConfirm}>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
